/* eslint-disable factorial/no-raw-html */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Avatar, Box, Button, Text } from '@factorialco/deprecated-design-system'
import DOMPurify from 'dompurify'
import type { ActivityLogsQuery } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { Comment } from './Changes/Comment'
import { Tag } from './Changes/Tag'
import { Updates } from './Changes/Updates'

type ActivityLogLog = ActivityLogsQuery['activityLog']['logsConnection']['nodes'][number]

interface Props {
  log: ActivityLogLog
}

export const Item = ({ log }: Props) => {
  const [moreButtonOpened, setMoreButtonOpened] = useState(false)
  const [isHover, setIsHover] = useState<boolean>(false)

  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const changes = log.changes.map((change) =>
    JSON.parse(change.replace(/=>/g, ':').replace(/"/g, '"'))
  )

  const goToLink = () => {
    navigate(String(log.link))
  }

  const goToAuthorProfile = (e: MouseEvent) => {
    e.stopPropagation()

    navigate(navigation.employees.id.fullPath({ id: Number(log.authorEmployeeId) }))
  }

  return (
    <Box
      flexDirection="row"
      gap="s8"
      paddingX="s24"
      paddingTop="s16"
      paddingBottom="s12"
      width="full"
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      {...(isHover && log.link && { cursor: 'pointer', background: 'grey200', onClick: goToLink })}
    >
      <Box data-testid="log-avatar" onClick={goToAuthorProfile}>
        <Avatar url={log.avatar} size="s24" label="" />
      </Box>
      <Box gap="s8" data-testid="log-description" width="full">
        <Box flexDirection="row" width="full" justifyContent="spaceBetween">
          <div
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(String(log.description)),
            }}
          />
        </Box>

        <Box gap="s8">
          {changes.map((change, index) => {
            if (index > 3 && !moreButtonOpened) return null

            if (change.type === 'comment') return <Comment key={index} value={change.value} />
            if (change.type === 'tag') return <Tag key={index} change={change} />

            return <Updates key={index} change={change} />
          })}

          {changes.length > 4 && (
            <Box data-testid="log-more-items-button">
              <Button type="secondary" onClick={() => setMoreButtonOpened(!moreButtonOpened)}>
                {moreButtonOpened ? i18n.t('common.show_less') : i18n.t('common.show_more')}
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      <Box data-testid="log-time" paddingLeft="s16">
        <Text.Normal size="100" color="grey600">
          {String(
            stringToDate(log.logDate, 'backend_datetime')
              .tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
              .format('HH:mm')
          )}
        </Text.Normal>
      </Box>
    </Box>
  )
}
